import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';

interface OnboardingContextType {
  isOpen: boolean;
  showAccountSetup: boolean;
  currentStep: number;
  completedSteps: string[];
  openOnboarding: () => void;
  closeOnboarding: () => void;
  openAccountSetup: () => void;
  closeAccountSetup: () => void;
  nextStep: () => void;
  prevStep: () => void;
  completeStep: (stepId: string) => void;
  skipOnboarding: () => void;
  completeAccountSetup: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkOnboardingStatus(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkOnboardingStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkOnboardingStatus = async (userId: string) => {
    // Check profile for PayPal onboarding status
    const { data: profile } = await supabase
      .from('profiles')
      .select('paypal_merchant_id, paypal_onboarding_status, paypal_payments_receivable')
      .eq('id', userId)
      .single();

    // Check onboarding progress
    const { data, error } = await supabase
      .from('onboarding_progress')
      .select('*')
      .eq('user_id', userId)
      .single();

    // Check for seller activity (listings or sell orders)
    const { data: sellOrders } = await supabase
      .from('orders')
      .select('id')
      .eq('user_id', userId)
      .eq('side', 'SELL')
      .limit(1);

    const { data: listings } = await supabase
      .from('items')
      .select('id')
      .eq('seller_id', userId)
      .limit(1);

    const hasSellerActivity = (sellOrders?.length || 0) > 0 || (listings?.length || 0) > 0;

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking onboarding:', error);
      return;
    }

    if (!data) {
      // New user - create onboarding record and show account setup wizard
      await supabase.from('onboarding_progress').insert({
        user_id: userId,
        completed: false,
        steps_completed: [],
        current_step: 0
      });
      
      // Show account setup wizard for new users (not the tutorial)
      setShowAccountSetup(true);
    } else if (!data.completed) {
      // Returning user who hasn't completed onboarding
      const steps = Array.isArray(data.steps_completed) ? data.steps_completed : [];
      setCompletedSteps(steps as string[]);
      setCurrentStep(data.current_step || 0);
      
      // Only show wizard if user has seller activity but hasn't completed PayPal
      const isSellerOnboarded = profile?.paypal_onboarding_status === 'COMPLETED' 
        && !!profile?.paypal_merchant_id 
        && profile?.paypal_payments_receivable === true;
      
      if (hasSellerActivity && !isSellerOnboarded) {
        setShowAccountSetup(true);
      }
      // Buyer-only users (no seller activity) don't need the wizard - they're ready to buy
    }
  };

  const openOnboarding = () => setIsOpen(true);
  const closeOnboarding = () => setIsOpen(false);
  const openAccountSetup = () => setShowAccountSetup(true);
  const closeAccountSetup = () => setShowAccountSetup(false);

  const nextStep = async () => {
    const newStep = currentStep + 1;
    setCurrentStep(newStep);
    
    if (user) {
      await supabase
        .from('onboarding_progress')
        .update({ current_step: newStep })
        .eq('user_id', user.id);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeStep = async (stepId: string) => {
    if (!completedSteps.includes(stepId)) {
      const newCompletedSteps = [...completedSteps, stepId];
      setCompletedSteps(newCompletedSteps);
      
      if (user) {
        await supabase
          .from('onboarding_progress')
          .update({ steps_completed: newCompletedSteps })
          .eq('user_id', user.id);
      }
    }
  };

  const skipOnboarding = async () => {
    if (user) {
      await supabase
        .from('onboarding_progress')
        .update({ completed: true })
        .eq('user_id', user.id);
    }
    setIsOpen(false);
    setShowAccountSetup(false);
  };

  const completeAccountSetup = async () => {
    if (user) {
      await supabase
        .from('onboarding_progress')
        .update({ completed: true, steps_completed: ['welcome', 'profile', 'payments'] })
        .eq('user_id', user.id);
    }
    setShowAccountSetup(false);
  };

  return (
    <OnboardingContext.Provider
      value={{
        isOpen,
        showAccountSetup,
        currentStep,
        completedSteps,
        openOnboarding,
        closeOnboarding,
        openAccountSetup,
        closeAccountSetup,
        nextStep,
        prevStep,
        completeStep,
        skipOnboarding,
        completeAccountSetup
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
